import { z } from 'zod';

export const OrganizationTaxClassificationSchema = z.enum([
  'SIMPLES_NACIONAL',
  'LUCRO_PRESUMIDO',
  'LUCRO_REAL',
]);

export type OrganizationTaxClassification = z.infer<typeof OrganizationTaxClassificationSchema>;

export const OrganizationLegalRepresentativeRoleSchema = z.enum([
  'PARTNER_ADMIN',
  'DIRECTOR',
  'ATTORNEY',
  'ACCOUNTANT',
]);

export type OrganizationLegalRepresentativeRole = z.infer<
  typeof OrganizationLegalRepresentativeRoleSchema
>;

const optionalNullableText = z.string().nullable().optional();

export const OrganizationHeadquartersWriteSchema = z
  .object({
    countryId: z.uuid().nullable().optional(),
    stateId: z.uuid().nullable().optional(),
    cityId: z.uuid().nullable().optional(),
    stateText: optionalNullableText,
    cityText: optionalNullableText,
    zipCode: z
      .string()
      .transform((value) => value.replace(/\D/g, ''))
      .refine((value) => value.length === 8, 'ZIP must have 8 digits'),
    street: z.string().trim().min(1),
    number: optionalNullableText,
    complement: optionalNullableText,
    neighborhood: optionalNullableText,
  })
  .refine(
    (hq) => {
      const hasCityId = hq.cityId !== undefined && hq.cityId !== null;
      const cityText = hq.cityText;
      const hasCityText = cityText !== undefined && cityText !== null && cityText.trim().length > 0;
      return hasCityId || hasCityText;
    },
    { message: 'City is required', path: ['cityId'] },
  );

export type OrganizationHeadquartersWrite = z.infer<typeof OrganizationHeadquartersWriteSchema>;

export const OrganizationLegalRepresentativeWriteSchema = z.object({
  name: optionalNullableText,
  cpf: optionalNullableText,
  role: OrganizationLegalRepresentativeRoleSchema.nullable().optional(),
});

export type OrganizationLegalRepresentativeWrite = z.infer<
  typeof OrganizationLegalRepresentativeWriteSchema
>;

export const OrganizationCadastralWriteFieldsSchema = z.object({
  tradeName: optionalNullableText,
  stateRegistration: optionalNullableText,
  municipalRegistration: optionalNullableText,
  taxClassification: OrganizationTaxClassificationSchema.nullable().optional(),
  phone: optionalNullableText,
  email: z
    .union([z.email(), z.literal('')])
    .nullable()
    .optional(),
  headquarters: OrganizationHeadquartersWriteSchema,
  legalRepresentative: OrganizationLegalRepresentativeWriteSchema.nullable().optional(),
});

export type OrganizationCadastralWriteFields = z.infer<
  typeof OrganizationCadastralWriteFieldsSchema
>;

export const OrganizationHeadquartersReadSchema = z.object({
  countryId: z.uuid().nullable(),
  stateId: z.uuid().nullable(),
  cityId: z.uuid().nullable(),
  stateText: z.string().nullable(),
  cityText: z.string().nullable(),
  zipCode: z.string().nullable(),
  street: z.string().nullable(),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  neighborhood: z.string().nullable(),
});

export type OrganizationHeadquartersRead = z.infer<typeof OrganizationHeadquartersReadSchema>;

export const OrganizationLegalRepresentativeReadSchema = z.object({
  name: z.string().nullable(),
  cpf: z.string().nullable(),
  role: OrganizationLegalRepresentativeRoleSchema.nullable(),
});

export type OrganizationLegalRepresentativeRead = z.infer<
  typeof OrganizationLegalRepresentativeReadSchema
>;

export const OrganizationCadastralReadFieldsSchema = z.object({
  tradeName: z.string().nullable(),
  stateRegistration: z.string().nullable(),
  municipalRegistration: z.string().nullable(),
  taxClassification: OrganizationTaxClassificationSchema.nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  headquarters: OrganizationHeadquartersReadSchema,
  legalRepresentative: OrganizationLegalRepresentativeReadSchema.nullable(),
});

export type OrganizationCadastralReadFields = z.infer<typeof OrganizationCadastralReadFieldsSchema>;

export const EMPTY_ORGANIZATION_HEADQUARTERS: OrganizationHeadquartersRead = {
  countryId: null,
  stateId: null,
  cityId: null,
  stateText: null,
  cityText: null,
  zipCode: null,
  street: null,
  number: null,
  complement: null,
  neighborhood: null,
};

export function emptyOrganizationCadastralReadFields(): OrganizationCadastralReadFields {
  return {
    tradeName: null,
    stateRegistration: null,
    municipalRegistration: null,
    taxClassification: null,
    phone: null,
    email: null,
    headquarters: { ...EMPTY_ORGANIZATION_HEADQUARTERS },
    legalRepresentative: null,
  };
}
